import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Copy, Trash2, Loader2, Layers, CalendarClock, Palmtree, CheckCircle2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";

function fmtRange(start, end) {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const mo = (d) => d.toLocaleDateString("en-US", { month: "short" });
  const sameMonth = s.getMonth() === e.getMonth();
  const left = `${mo(s)} ${s.getDate()}`;
  const right = sameMonth ? `${e.getDate()}` : `${mo(e)} ${e.getDate()}`;
  return `${left} – ${right}, ${e.getFullYear()}`;
}

const Stat = ({ label, value, sub }) => (
  <div className="px-4 py-3">
    <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
    <div className="head text-2xl font-semibold text-ink mt-0.5 leading-none">{value}</div>
    {sub && <div className="text-[11px] text-faint mt-1">{sub}</div>}
  </div>
);

export default function TermPlanner() {
  const { requirements, skeletons } = useApp();
  const [term, setTerm] = useState(null);
  const [weeks, setWeeks] = useState(36);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setTerm(await Endpoints.term());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    if (!term) return null;
    const included = (term.weeks || []).filter((w) => !w.excluded);
    const excluded = (term.weeks || []).length - included.length;
    const last = [...(term.weeks || [])].filter((w) => !w.excluded).slice(-1)[0] || (term.weeks || [])[term.weeks.length - 1];
    return {
      included: included.length,
      excluded,
      reqInstances: term.requirement_count * included.length,
      skInstances: term.skeleton_count * included.length,
      endDate: last ? last.end_date : null,
    };
  }, [term]);

  const clone = async () => {
    const n = Math.max(1, Math.min(52, Number(weeks) || 1));
    setBusy(true);
    try {
      const res = await Endpoints.cloneTerm(n);
      setTerm(res);
      toast.success(`Master week cloned across ${n} weeks`, {
        description: `${res.requirement_count} blocks × ${n} weeks duplicated forward.`,
      });
    } catch (e) {
      toast.error("Clone failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (i) => {
    try {
      setTerm(await Endpoints.toggleTermWeek(i));
    } catch (e) {
      toast.error("Could not update week", { description: e?.response?.data?.detail || e.message });
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await Endpoints.clearTerm();
      setTerm(null);
      toast.success("Term plan cleared");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-primary" /> <span className="text-sm">Loading term plan…</span>
      </div>
    );
  }

  return (
    <div className="animate-fadeup">
      <PageHeader
        title="Term Planner"
        subtitle="Roll the authored master week forward across the whole term. This simply duplicates the master-week structure into future weeks — no re-solving. Exclude any holiday / vacation weeks with one click."
      >
        {term && (
          <Button variant="danger" size="sm" onClick={clear} disabled={busy} data-testid="clear-term-btn">
            <Trash2 className="w-4 h-4" /> Clear term
          </Button>
        )}
      </PageHeader>

      {/* clone control */}
      <Card className="mb-6">
        <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="w-full sm:w-52">
            <Input
              label="Number of weeks"
              type="number"
              min="1"
              max="52"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              data-testid="term-weeks-input"
            />
          </div>
          <Button variant="primary" size="md" onClick={clone} disabled={busy} data-testid="clone-term-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
            {term ? "Re-clone master week" : "Clone master week forward"}
          </Button>
          <div className="text-[11px] text-faint sm:ml-auto max-w-xs leading-relaxed">
            Snapshotting <span className="text-ink">{requirements.length}</span> requirement blocks and{" "}
            <span className="text-ink">{skeletons.length}</span> shift skeletons from the current master week.
          </div>
        </CardContent>
      </Card>

      {!term && (
        <div className="text-center py-20 border border-dashed border-line rounded-sm bg-surface/50" data-testid="term-empty-state">
          <CalendarClock className="w-8 h-8 text-faint mx-auto mb-3" />
          <p className="text-sm text-muted">No term plan yet.</p>
          <p className="text-[12px] text-faint mt-1">Set a week count above and clone the master week to build out the term.</p>
        </div>
      )}

      {term && stats && (
        <>
          {/* summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line rounded-sm overflow-hidden border border-line mb-6" data-testid="term-summary">
            <div className="bg-surface"><Stat label="Active weeks" value={stats.included} sub={stats.excluded ? `${stats.excluded} excluded (holiday)` : "none excluded"} /></div>
            <div className="bg-surface"><Stat label="Term ends" value={stats.endDate ? new Date(`${stats.endDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"} sub={stats.endDate ? new Date(`${stats.endDate}T00:00:00`).getFullYear() : ""} /></div>
            <div className="bg-surface"><Stat label="Requirement instances" value={stats.reqInstances.toLocaleString()} sub={`${term.requirement_count} blocks / week`} /></div>
            <div className="bg-surface"><Stat label="Shift skeletons" value={stats.skInstances.toLocaleString()} sub={`${term.skeleton_count} shifts / week`} /></div>
          </div>

          <div className="flex items-center gap-2 mb-3 text-[11px] text-muted">
            <Layers className="w-3.5 h-3.5 text-primary" />
            <span>Click a week to toggle it between <span className="text-success">Active</span> and <span className="text-warning">Holiday</span> (excluded).</span>
          </div>

          {/* week grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
            {term.weeks.map((w) => (
              <button
                key={w.index}
                type="button"
                onClick={() => toggle(w.index)}
                data-testid={`term-week-toggle-${w.index}`}
                className={`text-left rounded-sm border px-3 py-3 transition-colors duration-150 ${
                  w.excluded
                    ? "bg-surface2/40 border-line opacity-60 hover:opacity-90"
                    : "bg-surface border-line hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="mono text-[11px] text-faint">WEEK {String(w.index + 1).padStart(2, "0")}</span>
                  {w.excluded ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-warning border border-warning/40 bg-warning/10 px-1.5 py-0.5 rounded">
                      <Palmtree className="w-3 h-3" /> Holiday
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] text-success border border-success/40 bg-success/10 px-1.5 py-0.5 rounded">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <CalendarDays className="w-4 h-4 text-muted shrink-0" />
                  <span className={`text-sm ${w.excluded ? "text-muted line-through" : "text-ink"}`}>{fmtRange(w.start_date, w.end_date)}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
